import { ExternalLink, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// HubSpot record links (portal 24228132): 0-3 = deals, 0-5 = tickets
const HUBSPOT_PORTAL_ID = "24228132";
const dealUrl = (id: string) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-3/${id}`;
const ticketUrl = (id: string) =>
  `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-5/${id}`;

// Repair/Resize Service Pipeline stage labels (HubSpot pipeline id "0")
const REPAIR_STAGE_LABELS: Record<string, string> = {
  "1": "New Repair/Resize Request",
  "2": "Inbound",
  "3": "Production",
  "992139048": "Re-order",
  "992139049": "Re-design/Re-cast",
  "1018004034": "FPM Complete",
  "1083925139": "Quality Inspection",
  "74252675": "Final Inspection",
  "74252676": "Shipping",
  "4": "Picked Up/Order Complete",
  "144005032": "No Response",
};

export interface OpsDrillDownRequest {
  metric: string;
  /** Fiscal quarter label ("Q1"); omitted = whole fiscal year. */
  quarter?: string;
  label: string;
}

interface DrillDealRow {
  hubspot_deal_id: string;
  dealname: string | null;
  order_date: string | null;
  date_product_complete: string | null;
  manufacture_deadline: string | null;
  delivered_on_time: string | null;
  qcf_entered_date: string | null;
  qcf_reason: string | null;
  type_of_error: string | null;
  accuracy_error: string | null;
  error_notes: string | null;
}

interface DrillTicketRow {
  hubspot_ticket_id: string;
  subject: string | null;
  createdate: string | null;
  moved_to_production_date: string | null;
  completed_date: string | null;
  original_manufacturer: string | null;
  pipeline_stage: string | null;
  days_to_complete: number | null;
}

type DrillResponse =
  | { kind: "deals"; total: number; limit: number; rows: DrillDealRow[] }
  | { kind: "tickets"; total: number; limit: number; rows: DrillTicketRow[] };

interface OpsDrillDownSheetProps {
  dashboard: "manufacturing" | "operations";
  fiscalYear: number;
  drillDown: OpsDrillDownRequest | null;
  onClose: () => void;
}

/** Deal date columns are YYYY-MM-DD text — parse as local, never UTC. */
function formatDealDate(value: string | null): string {
  if (!value) return "—";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Ticket dates are ISO datetimes. */
function formatTicketDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function HubSpotLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 text-brand-navy hover:underline"
    >
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function YesNoBadge({
  yes,
  yesLabel,
  noLabel,
}: {
  yes: boolean;
  yesLabel: string;
  noLabel: string;
}) {
  return yes ? (
    <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 text-[10px]">
      {yesLabel}
    </Badge>
  ) : (
    <Badge variant="outline" className="border-red-500/40 text-red-700 text-[10px]">
      {noLabel}
    </Badge>
  );
}

export function OpsDrillDownSheet({
  dashboard,
  fiscalYear,
  drillDown,
  onClose,
}: OpsDrillDownSheetProps) {
  const [data, setData] = useState<DrillResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const fetchRows = useCallback(async () => {
    if (!drillDown) return;
    setLoading(true);
    setError(null);
    setSearch("");
    try {
      const params = new URLSearchParams({ metric: drillDown.metric, fy: String(fiscalYear) });
      if (drillDown.quarter) params.set("quarter", drillDown.quarter);
      const res = await fetch(`/api/ops-dashboards/${dashboard}/drill-down?${params}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setData((await res.json()) as DrillResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load records");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [drillDown, dashboard, fiscalYear]);

  useEffect(() => {
    if (drillDown) fetchRows();
  }, [drillDown, fetchRows]);

  const metric = drillDown?.metric ?? "";

  const q = search.toLowerCase();
  const dealRows =
    data?.kind === "deals"
      ? search
        ? data.rows.filter(
            (r) =>
              r.dealname?.toLowerCase().includes(q) || r.hubspot_deal_id.toLowerCase().includes(q),
          )
        : data.rows
      : [];
  const ticketRows =
    data?.kind === "tickets"
      ? search
        ? data.rows.filter(
            (r) =>
              r.subject?.toLowerCase().includes(q) || r.hubspot_ticket_id.toLowerCase().includes(q),
          )
        : data.rows
      : [];

  return (
    <Sheet open={!!drillDown} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2 font-serif">
            {drillDown?.label}
            {!loading && data && (
              <Badge variant="secondary" className="text-xs">
                {data.total.toLocaleString()}
              </Badge>
            )}
          </SheetTitle>
          {!loading && data && data.total > data.rows.length && (
            <p className="text-xs text-muted-foreground">
              Showing the first {data.rows.length.toLocaleString()} of {data.total.toLocaleString()}{" "}
              records.
            </p>
          )}
        </SheetHeader>

        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={data?.kind === "tickets" ? "Search tickets..." : "Search deals..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={`odd-sk-${i}`} className="h-10 w-full" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-sm text-destructive py-8">{error}</p>
        ) : data?.kind === "tickets" ? (
          ticketRows.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              {search ? "No tickets match your search." : "No tickets found."}
            </p>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs">Ticket</TableHead>
                    <TableHead className="text-xs">Opened</TableHead>
                    {metric === "repairResizeDays" ? (
                      <>
                        <TableHead className="text-xs">In Production</TableHead>
                        <TableHead className="text-xs">Completed</TableHead>
                        <TableHead className="text-xs text-right">Days</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-xs">Stage</TableHead>
                        <TableHead className="text-xs">Manufacturer</TableHead>
                      </>
                    )}
                    <TableHead className="text-xs w-[50px]">HS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ticketRows.map((t) => (
                    <TableRow key={t.hubspot_ticket_id}>
                      <TableCell className="text-xs max-w-[220px] truncate font-medium">
                        {t.subject ?? t.hubspot_ticket_id}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatTicketDate(t.createdate)}
                      </TableCell>
                      {metric === "repairResizeDays" ? (
                        <>
                          <TableCell className="text-xs whitespace-nowrap">
                            {formatDealDate(t.moved_to_production_date)}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {formatTicketDate(t.completed_date)}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {t.days_to_complete !== null
                              ? Number(t.days_to_complete).toFixed(1)
                              : "—"}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-xs whitespace-nowrap">
                            {REPAIR_STAGE_LABELS[t.pipeline_stage ?? ""] ?? t.pipeline_stage ?? "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {t.original_manufacturer ?? "—"}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-xs">
                        <HubSpotLink href={ticketUrl(t.hubspot_ticket_id)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        ) : dealRows.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {search ? "No deals match your search." : "No deals found."}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs">Deal</TableHead>
                  {(metric === "capacity" ||
                    (dashboard === "manufacturing" && metric === "otd")) && (
                    <>
                      <TableHead className="text-xs">Completed</TableHead>
                      <TableHead className="text-xs">MFG Deadline</TableHead>
                      {metric === "otd" && <TableHead className="text-xs">On Time</TableHead>}
                    </>
                  )}
                  {metric === "fpy" && (
                    <>
                      <TableHead className="text-xs">Order Date</TableHead>
                      <TableHead className="text-xs">Entered QCF</TableHead>
                      <TableHead className="text-xs">QCF Reason</TableHead>
                    </>
                  )}
                  {dashboard === "operations" && metric === "otd" && (
                    <>
                      <TableHead className="text-xs">Order Date</TableHead>
                      <TableHead className="text-xs">Delivered On Time</TableHead>
                    </>
                  )}
                  {(metric === "accuracyRate" || metric === "qualityRate") && (
                    <>
                      <TableHead className="text-xs">Order Date</TableHead>
                      <TableHead className="text-xs">Error</TableHead>
                      <TableHead className="text-xs">Notes</TableHead>
                    </>
                  )}
                  <TableHead className="text-xs w-[50px]">HS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dealRows.map((d) => (
                  <TableRow key={d.hubspot_deal_id}>
                    <TableCell className="text-xs max-w-[220px] truncate font-medium">
                      {d.dealname ?? d.hubspot_deal_id}
                    </TableCell>
                    {(metric === "capacity" ||
                      (dashboard === "manufacturing" && metric === "otd")) && (
                      <>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.date_product_complete)}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.manufacture_deadline)}
                        </TableCell>
                        {metric === "otd" && (
                          <TableCell className="text-xs">
                            {d.date_product_complete && d.manufacture_deadline ? (
                              <YesNoBadge
                                yes={d.date_product_complete <= d.manufacture_deadline}
                                yesLabel="On time"
                                noLabel="Late"
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}
                      </>
                    )}
                    {metric === "fpy" && (
                      <>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.order_date)}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.qcf_entered_date)}
                        </TableCell>
                        <TableCell className="text-xs max-w-[160px] truncate">
                          {d.qcf_reason ?? "—"}
                        </TableCell>
                      </>
                    )}
                    {dashboard === "operations" && metric === "otd" && (
                      <>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.order_date)}
                        </TableCell>
                        <TableCell className="text-xs">
                          <YesNoBadge
                            yes={d.delivered_on_time === "Yes"}
                            yesLabel="Yes"
                            noLabel="No"
                          />
                        </TableCell>
                      </>
                    )}
                    {(metric === "accuracyRate" || metric === "qualityRate") && (
                      <>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDealDate(d.order_date)}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {metric === "accuracyRate"
                            ? (d.accuracy_error ?? d.type_of_error ?? "—")
                            : (d.type_of_error ?? "—")}
                        </TableCell>
                        <TableCell className="text-xs max-w-[180px] truncate">
                          {d.error_notes ?? "—"}
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-xs">
                      <HubSpotLink href={dealUrl(d.hubspot_deal_id)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
